import axiosInstance, { axiosPublic } from './axiosInstance';

export async function getCampaignSections() {
  const response = await axiosPublic.get('/v1/campaigns/sections');
  return response.data?.data ?? response.data;
}

export async function getAllCampaigns({ page = 0, size = 20, sort = 'createdAt', isAsc = false } = {}) {
  const response = await axiosPublic.get('/v1/campaigns', {
    params: { page, size, sort, isAsc },
  });
  return response.data?.data ?? response.data;
}

/**
 * 목록 전체를 모아서 반환한다.
 *
 * 서버가 sort/isAsc 파라미터를 무시하기 때문에(항상 동일 순서로 응답) 정렬은 클라이언트에서 한다.
 * 전체 건수가 수백 건 수준이라 한 번에 받아두고 필터·정렬·검색을 로컬에서 처리하는 편이
 * 페이지마다 왕복하는 것보다 빠르고 정확하다. 서버 정렬/검색이 열리면 이 함수만 교체하면 된다.
 */
const POOL_PAGE_SIZE = 200;
const POOL_MAX_PAGES = 5; // 안전장치 — 최대 1000건

/**
 * 목록 캐시.
 * 목록 → 상세 → 뒤로가기를 할 때마다 전체(약 67KB)를 다시 받지 않도록
 * 진행 중인 요청과 결과를 모듈 수준에서 재사용한다.
 * 체험단 모집은 분 단위로 바뀌지 않으므로 TTL은 넉넉히 잡는다.
 */
const POOL_TTL = 5 * 60 * 1000;
let poolCache = null;      // { at: number, promise: Promise<Campaign[]> }

export function invalidateCampaignPool() {
  poolCache = null;
}

export async function getCampaignPool() {
  if (poolCache && Date.now() - poolCache.at < POOL_TTL) return poolCache.promise;

  const promise = fetchCampaignPool();
  poolCache = { at: Date.now(), promise };
  // 실패한 응답까지 5분간 붙잡고 있으면 재시도가 막힌다
  promise.catch(() => { poolCache = null; });
  return promise;
}

async function fetchCampaignPool() {
  const first = await getAllCampaigns({ page: 0, size: POOL_PAGE_SIZE });
  const paged = first?.campaigns ?? {};
  const content = [...(paged.content ?? [])];

  const serverPages = paged.totalPages ?? 1;
  if (serverPages > POOL_MAX_PAGES) {
    // 조용히 잘리면 목록에서 사라진 이유를 알 수 없다
    console.warn(
      `[campaigns] 전체 ${serverPages}페이지 중 ${POOL_MAX_PAGES}페이지만 가져옵니다. ` +
      '서버 정렬·검색으로 전환할 시점입니다.'
    );
  }
  const totalPages = Math.min(serverPages, POOL_MAX_PAGES);
  if (totalPages > 1) {
    const rest = await Promise.all(
      Array.from({ length: totalPages - 1 }, (_, i) =>
        getAllCampaigns({ page: i + 1, size: POOL_PAGE_SIZE })
      )
    );
    rest.forEach((r) => content.push(...(r?.campaigns?.content ?? [])));
  }
  return content;
}

export async function getCampaignDetail(campaignId) {
  const response = await axiosInstance.get(`/v1/campaigns/${campaignId}`);
  return response.data?.data ?? response.data;
}

export async function getScrappedCampaigns() {
  const response = await axiosInstance.get('/v1/campaigns/me/scraps');
  return response.data?.data ?? response.data;
}

export async function scrapCampaign(campaignId) {
  await axiosInstance.post(`/v1/campaigns/${campaignId}/scraps`);
}

export async function unscrapCampaign(campaignId) {
  await axiosInstance.delete(`/v1/campaigns/${campaignId}/scraps`);
}
